import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, MessageCircle, Phone } from 'lucide-react';

// ── Images: reusing what's already in the project ──
const IMG_TODDLER    = 'https://images.unsplash.com/photo-1564429097439-e400382dc893?w=700&q=80';
const IMG_PRIMARY    = 'https://images.unsplash.com/photo-1533222535026-754c501569dd?w=700&q=85';
const IMG_ELEMENTARY = 'https://images.pexels.com/photos/31864407/pexels-photo-31864407.jpeg?auto=compress&cs=tinysrgb&w=700';

const WA_URL  = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20learning%20about%20your%20programs';
const WA_FEES = 'https://wa.me/919246887272?text=Hi%2C%20could%20you%20share%20the%20fee%20details%20for%20your%20programs%3F';

const primaryAreas = [
  { label: 'Practical Life', desc: 'Pouring, spooning, buttoning, polishing, cookery — real work with real purpose, building concentration, coordination and independence.' },
  { label: 'Sensorial', desc: 'Materials that refine every sense, quietly laying the base for mathematics and language.' },
  { label: 'Language', desc: 'A strong spoken foundation first (including Show & Tell from 2.5!), then writing arrives naturally around four — in cursive — followed by joyful reading.' },
  { label: 'Mathematics', desc: 'Numbers held in the hand as beads and rods before they\'re ever written; the sensitive period for maths (around 3.5) is met, not missed.' },
  { label: 'Culture', desc: 'The universe, maps and continents, plants and animals, music, and the stories of every festival, of every faith.' },
];

const elementaryLife = [
  { label: 'Small-group learning', desc: '4–5 children explore together, learning to cooperate, to give help and to take help.' },
  { label: 'The great questions', desc: 'How did the universe begin? How did humans come to be? Every subject grows out of genuine wonder, traced to its roots.' },
  { label: 'From concrete to abstract', desc: 'Children gradually move from materials into pure abstraction on paper — algebraic identities included — because they once held these ideas in their hands.' },
  { label: 'Care of the environment', desc: 'Sweeping, washing plates, weeding, gardening: dignity of work, practised daily.' },
  { label: 'The wider world', desc: 'Field trips 3–4 times a year, monthly workshops, and visiting parents who share how they built their own careers.' },
  { label: 'Body, voice and spirit', desc: 'Yoga, karate, classical vocal music, public speaking, and Bhagavad Gita classes from age five onward.' },
];

const practicalDetails = [
  { label: 'School hours', value: 'Toddler: 9:00 am – 12:30 pm\nPrimary: 9:00 am – 3:00 pm\nElementary: 9:00 am – 3:30 pm' },
  { label: 'Working days', value: 'Monday to Friday' },
  { label: 'Adult–child ratio', value: '1 : 15, with two AMI-trained guides in every environment' },
  { label: 'Uniform', value: 'None — children come as themselves' },
  { label: 'Meals & snacks', value: 'Healthy tiffin from home. Packaged food, chips and junk food are not allowed.' },
  { label: 'Admissions', value: 'Year-round — we go by the child turning 2 years 6 months' },
  { label: 'Fees', value: 'Shared on enquiry' },
];

export default function Programs() {
  return (
    <div className="pt-16">
      <Helmet>
        <title>Programs: Toddler, Primary &amp; Elementary (Ages 2–12) | Sri Aurobindo Montessori, Hyderabad</title>
        <meta name="description" content="Montessori programs in West Marredpally, Secunderabad — Toddler (2–2.5), Primary (2.5–6) and Elementary (6–12). One-to-one learning, AMI-trained guides, and a seamless journey from first steps to abstract thinking." />
      </Helmet>

      {/* ── BANNER ── */}
      <section className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Ages 2 – 12</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
            One journey,<br className="hidden sm:block" /> three environments
          </h1>
          <p className="text-blue-200 text-base max-w-xl mx-auto">
            Every stage is built for who your child is right now — and quietly prepares who they are about to become. Nothing is rushed; nothing is wasted.
          </p>
        </div>

        {/* Mobile age-band quick nav */}
        <div className="mt-8 flex justify-center gap-3 px-4 lg:hidden">
          {[
            { label: '2 – 2.5 yrs', anchor: '#toddler' },
            { label: '2.5 – 6 yrs', anchor: '#primary' },
            { label: '6 – 12 yrs', anchor: '#elementary' },
          ].map((tab) => (
            <a
              key={tab.anchor}
              href={tab.anchor}
              className="bg-white/15 border border-white/30 text-white text-xs font-bold rounded-full px-4 py-2 hover:bg-white/25 transition-colors"
            >
              {tab.label}
            </a>
          ))}
        </div>
      </section>

      {/* ── TODDLER ── */}
      <section id="toddler" className="py-20 bg-[#EFF6FF]" data-testid="program-section-toddler">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">

            {/* Image */}
            <div className="rounded-3xl overflow-hidden shadow-[0_8px_40px_rgba(30,58,138,0.12)]">
              <img src={IMG_TODDLER} alt="Toddler environment — placeholder for school photo" className="w-full h-80 object-cover" loading="lazy" />
            </div>

            {/* Content */}
            <div>
              <span className="inline-block bg-[#DBEAFE] text-[#1E3A8A] text-xs font-bold rounded-full px-4 py-1.5 mb-4">2 – 2.5 years</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-5 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Toddler — a gentle beginning
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                This is your child's very first step away from family — and we treat it with all the tenderness that deserves. The Toddler environment has one job above all: helping your child settle, feel safe, and discover that the world beyond home is a warm and interesting place.
              </p>
              <p className="text-slate-600 font-semibold text-sm mb-4">In this stage your child will:</p>
              <ul className="space-y-3 mb-7">
                {[
                  'Settle into school life at their own gentle pace, with unhurried, affectionate adults',
                  'Be immersed in rich spoken language — rhymes, names of real things, conversation, stories',
                  'Begin simple practical activities that build coordination and the first taste of "I did it myself!"',
                ].map((pt, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 text-sm">
                    <CheckCircle size={16} className="text-[#60A5FA] mt-0.5 flex-shrink-0" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>

              {/* Parent note */}
              <div className="bg-white rounded-2xl px-6 py-5 border-l-4 border-[#60A5FA] mb-7 shadow-sm">
                <p className="text-xs font-bold text-[#60A5FA] uppercase tracking-wide mb-2">Parent note</p>
                <p className="text-slate-600 text-sm leading-relaxed italic">
                  Separation is a milestone for you too. We'll guide you through it — most families are surprised how quickly the tears (theirs and yours) turn into a happy morning wave.
                </p>
              </div>

              <Link
                to="/admissions"
                data-testid="program-enquire-toddler"
                className="bg-[#1E3A8A] text-white rounded-full px-7 py-3.5 font-bold text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm inline-flex items-center gap-2"
              >
                Enquire for Toddler <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRIMARY ── */}
      <section id="primary" className="py-20 bg-white" data-testid="program-section-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">

            {/* Content */}
            <div>
              <span className="inline-block bg-[#DBEAFE] text-[#1E3A8A] text-xs font-bold rounded-full px-4 py-1.5 mb-4">2.5 – 6 years</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-5 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Primary — the absorbent<br className="hidden sm:block" /> mind at work
              </h2>
              <p className="text-slate-600 leading-relaxed mb-7">
                These are the most absorbent years of your child's entire life — and we honour them with the full, authentic Montessori Primary environment. Learning here is one-to-one: the guide presents each material to each child at exactly the right moment, then steps back.
              </p>

              <p className="text-slate-600 font-semibold text-sm mb-4">The five areas your child moves through, at their own pace:</p>
              <div className="space-y-4 mb-7">
                {primaryAreas.map((area, i) => (
                  <div key={i} className="flex items-start gap-4 bg-[#F4F7FB] rounded-2xl px-5 py-4">
                    <div className="w-6 h-6 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-xs font-black flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-bold text-[#1E3A8A] text-sm mb-0.5">{area.label}</p>
                      <p className="text-slate-500 text-xs leading-relaxed">{area.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* What gains */}
              <div className="bg-[#EFF6FF] rounded-2xl px-6 py-5 border-l-4 border-[#1E3A8A] mb-7">
                <p className="text-xs font-bold text-[#1E3A8A] uppercase tracking-wide mb-2">What your child gains by six</p>
                <p className="text-slate-700 text-sm leading-relaxed">
                  Independence in daily life, clear confident speech, deep concentration, early writing and reading, a genuine love of numbers — and academically, the equivalent of Class 1 complete. But more than any of it: <strong>a mind in the habit of deciding.</strong>
                </p>
              </div>

              <Link
                to="/admissions"
                data-testid="program-enquire-primary"
                className="bg-[#1E3A8A] text-white rounded-full px-7 py-3.5 font-bold text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm inline-flex items-center gap-2"
              >
                Enquire for Primary <ArrowRight size={16} />
              </Link>
            </div>

            {/* Image */}
            <div className="rounded-3xl overflow-hidden shadow-[0_8px_40px_rgba(30,58,138,0.12)]">
              <img src={IMG_PRIMARY} alt="Primary environment — placeholder for school photo" className="w-full h-[500px] object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* ── ELEMENTARY ── */}
      <section id="elementary" className="py-20 bg-[#F4F7FB]" data-testid="program-section-elementary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">

            {/* Image */}
            <div className="rounded-3xl overflow-hidden shadow-[0_8px_40px_rgba(30,58,138,0.12)]">
              <img src={IMG_ELEMENTARY} alt="Elementary environment — placeholder for school photo" className="w-full h-80 object-cover" loading="lazy" />
            </div>

            {/* Content */}
            <div>
              <span className="inline-block bg-[#DBEAFE] text-[#1E3A8A] text-xs font-bold rounded-full px-4 py-1.5 mb-4">6 – 12 years</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-5 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Elementary — from<br className="hidden sm:block" /> "what" to "why"
              </h2>
              <p className="text-slate-600 leading-relaxed mb-7">
                At six, a beautiful transformation happens. The child who was content in her own work and her own family now hungers for friends, groups, and big questions. Our Elementary environment is designed exactly for this.
              </p>

              <p className="text-slate-600 font-semibold text-sm mb-4">Life in Elementary:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                {elementaryLife.map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl px-5 py-4 shadow-[0_2px_12px_rgba(59,130,246,0.06)]">
                    <p className="font-bold text-[#1E3A8A] text-xs mb-1">{item.label}</p>
                    <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* What gains */}
              <div className="bg-white rounded-2xl px-6 py-5 border-l-4 border-[#1E3A8A] mb-7 shadow-sm">
                <p className="text-xs font-bold text-[#1E3A8A] uppercase tracking-wide mb-2">What your child gains</p>
                <p className="text-slate-700 text-sm leading-relaxed">
                  The ability to think logically and independently, to research, to present ideas fearlessly, to work in a team — and to walk into any school afterwards and quietly excel.
                </p>
                <p className="text-slate-400 text-xs italic mt-2">
                  One of ours topped Class 10 at a leading Hyderabad school; parents tell us again and again that Aurobindo children stand out wherever they go.
                </p>
              </div>

              <Link
                to="/admissions"
                data-testid="program-enquire-elementary"
                className="bg-[#1E3A8A] text-white rounded-full px-7 py-3.5 font-bold text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm inline-flex items-center gap-2"
              >
                Enquire for Elementary <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── MOVING BETWEEN STAGES ── */}
      <section className="py-16 bg-white" data-testid="transitions-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">No Exams, No Rushing</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Moving between stages
          </h2>
          <p className="text-slate-600 leading-relaxed">
            There are no exams to "pass" from one environment to the next. Guides observe each child's readiness — developmental, not chronological — and transitions are made gently, in consultation with parents. Montessori never boxes a child into an age; every child's path is their own.
          </p>
        </div>
      </section>

      {/* ── PRACTICAL DETAILS ── */}
      <section className="py-16 bg-[#F4F7FB]" data-testid="practical-details-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">At a Glance</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-8" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Practical details
          </h2>
          <div className="space-y-0 rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(30,58,138,0.08)] border border-blue-50">
            {practicalDetails.map((item, i) => (
              <div
                key={i}
                data-testid={`detail-row-${i}`}
                className={`grid grid-cols-5 gap-4 px-6 py-5 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9FBFF]'} ${i < practicalDetails.length - 1 ? 'border-b border-blue-50' : ''}`}
              >
                <p className="col-span-2 font-bold text-[#1E3A8A] text-sm">{item.label}</p>
                <p className="col-span-3 text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                  {item.label === 'Fees' ? (
                    <span className="inline-flex items-center gap-3 flex-wrap">
                      Shared on enquiry &mdash;{' '}
                      <a
                        href={WA_FEES}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="fees-whatsapp-link"
                        className="inline-flex items-center gap-1.5 bg-[#25D366] text-white rounded-full px-4 py-1 text-xs font-bold hover:bg-[#128C7E] transition-colors"
                      >
                        <MessageCircle size={12} /> WhatsApp us for details
                      </a>
                    </span>
                  ) : item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 bg-[#1E3A8A]" data-testid="programs-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Visit Us</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Find the right start<br className="hidden sm:block" /> for your child
          </h2>
          <p className="text-blue-200 text-base mb-10 max-w-xl mx-auto">
            Not sure which program fits? That's exactly what a visit is for. Come, walk through all three environments, and let's talk about your child.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions#enquiry-form"
              data-testid="programs-tour-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Book a Tour <ArrowRight size={16} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="programs-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <a
              href="tel:9246887272"
              data-testid="programs-call-btn"
              className="border-2 border-white/40 text-white rounded-full px-8 py-4 font-black text-sm hover:border-white hover:bg-white/10 transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <Phone size={16} /> Call Us
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
